import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { safeGetItem, safeSetItem, safeRemoveItem } from '../utils/safeStorage';
import { useIsMobile } from '../hooks/useIsMobile';

export type SidebarMode = 'expanded' | 'collapsed' | 'hidden';

interface SidebarContextType {
  // Primary state
  sidebarMode: SidebarMode;
  setSidebarMode: (mode: SidebarMode) => void;
  cycleSidebarMode: () => void;

  // Backward compat (derived)
  isCollapsed: boolean;
  toggleSidebar: () => void;

  // Desktop overlay (hidden mode)
  isDesktopOverlayOpen: boolean;
  openDesktopOverlay: () => void;
  closeDesktopOverlay: () => void;

  // Mobile
  isMobile: boolean;
  isMobileOpen: boolean;
  openMobile: () => void;
  closeMobile: () => void;
}

const SidebarContext = createContext<SidebarContextType | undefined>(undefined);

// --- Scroll lock helpers (shared between mobile + desktop overlay) ---
function lockBodyScroll(scrollYRef: React.MutableRefObject<number>) {
  scrollYRef.current = window.scrollY;
  document.body.style.overflow = 'hidden';
  document.body.style.position = 'fixed';
  document.body.style.width = '100%';
  document.body.style.top = `-${scrollYRef.current}px`;
}

function unlockBodyScroll(scrollYRef: React.MutableRefObject<number>) {
  document.body.style.overflow = '';
  document.body.style.position = '';
  document.body.style.width = '';
  document.body.style.top = '';
  window.scrollTo(0, scrollYRef.current);
}

// --- localStorage migration ---
function initSidebarMode(): SidebarMode {
  const newVal = safeGetItem('sidebarMode');
  if (newVal === 'expanded' || newVal === 'collapsed' || newVal === 'hidden') {
    return newVal;
  }
  // Migrate from old boolean key
  const oldVal = safeGetItem('sidebarCollapsed');
  const migrated: SidebarMode = oldVal === 'true' ? 'collapsed' : 'expanded';
  safeSetItem('sidebarMode', migrated);
  if (oldVal !== null) {
    safeRemoveItem('sidebarCollapsed');
  }
  return migrated;
}

export const SidebarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [sidebarMode, setSidebarMode] = useState<SidebarMode>(initSidebarMode);

  const isMobile = useIsMobile(768);
  const isMobileRef = useRef(isMobile);
  useEffect(() => { isMobileRef.current = isMobile; }, [isMobile]);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDesktopOverlayOpen, setIsDesktopOverlayOpen] = useState(false);
  const scrollYRef = useRef(0);

  // Derived
  const isCollapsed = sidebarMode === 'collapsed';

  // --- cycleSidebarMode: expanded → collapsed → hidden → expanded ---
  const cycleSidebarMode = useCallback(() => {
    setSidebarMode(prev => {
      if (prev === 'expanded') return 'collapsed';
      if (prev === 'collapsed') return 'hidden';
      return 'expanded';
    });
  }, []);

  // --- toggleSidebar: backward compat, expanded <-> collapsed ---
  const toggleSidebar = useCallback(() => {
    setSidebarMode(prev => prev === 'expanded' ? 'collapsed' : 'expanded');
  }, []);

  // --- Mobile drawer ---
  const isMobileOpenRef = useRef(false);

  const openMobile = useCallback(() => {
    if (isMobileOpenRef.current) return;
    isMobileOpenRef.current = true;
    setIsMobileOpen(true);
    lockBodyScroll(scrollYRef);
  }, []);

  const closeMobile = useCallback(() => {
    if (!isMobileOpenRef.current) return;
    isMobileOpenRef.current = false;
    setIsMobileOpen(false);
    unlockBodyScroll(scrollYRef);
  }, []);

  // --- Desktop overlay ---
  const isDesktopOverlayOpenRef = useRef(false);

  const openDesktopOverlay = useCallback(() => {
    if (isDesktopOverlayOpenRef.current) return;
    isDesktopOverlayOpenRef.current = true;
    setIsDesktopOverlayOpen(true);
    lockBodyScroll(scrollYRef);
  }, []);

  const closeDesktopOverlay = useCallback(() => {
    if (!isDesktopOverlayOpenRef.current) return;
    isDesktopOverlayOpenRef.current = false;
    setIsDesktopOverlayOpen(false);
    unlockBodyScroll(scrollYRef);
  }, []);

  // --- Persist sidebarMode ---
  useEffect(() => {
    safeSetItem('sidebarMode', sidebarMode);
  }, [sidebarMode]);

  // --- Close desktop overlay when switching to mobile ---
  useEffect(() => {
    if (isMobile && isDesktopOverlayOpen) {
      closeDesktopOverlay();
    }
  }, [isMobile, isDesktopOverlayOpen, closeDesktopOverlay]);

  // --- Close mobile drawer when switching to desktop ---
  useEffect(() => {
    if (!isMobile && isMobileOpen) {
      closeMobile();
    }
  }, [isMobile, isMobileOpen, closeMobile]);

  // --- Escape key: close mobile drawer OR desktop overlay (uses refs to avoid re-registration) ---
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      if (isMobileRef.current && isMobileOpenRef.current) {
        closeMobile();
      } else if (!isMobileRef.current && isDesktopOverlayOpenRef.current) {
        closeDesktopOverlay();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [closeMobile, closeDesktopOverlay]);

  // --- Ctrl+B / Cmd+B: cycle sidebar mode ---
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 'b') {
        const tag = (e.target as HTMLElement)?.tagName;
        if (tag === 'INPUT' || tag === 'TEXTAREA' || (e.target as HTMLElement)?.isContentEditable) return;
        e.preventDefault();
        cycleSidebarMode();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [cycleSidebarMode]);

  return (
    <SidebarContext.Provider value={{
      sidebarMode,
      setSidebarMode,
      cycleSidebarMode,
      isCollapsed,
      toggleSidebar,
      isDesktopOverlayOpen,
      openDesktopOverlay,
      closeDesktopOverlay,
      isMobile,
      isMobileOpen,
      openMobile,
      closeMobile,
    }}>
      {children}
    </SidebarContext.Provider>
  );
};

export const useSidebar = () => {
  const context = useContext(SidebarContext);
  if (!context) {
    throw new Error('useSidebar must be used within a SidebarProvider');
  }
  return context;
};
